import React from 'react';
import { useDrop } from 'react-dnd';

export const MARKETING_COLLECTION_ITEM_DRAG_TYPE = 'MARKETING_COLLECTION_ITEM_DRAG_TYPE';

export type CatalogItemDragPayload = {
  itemId: number;
  sourceCollectionId: number;
  index: number;
};

type CatalogTableRowDropZoneProps = {
  targetCollectionId: number;
  onDropItem: (payload: CatalogItemDragPayload) => void;
  children: React.ReactElement<{ className?: string; ref?: React.Ref<HTMLTableRowElement> }>;
};

export function CatalogTableRowDropZone({ targetCollectionId, onDropItem, children }: CatalogTableRowDropZoneProps) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: MARKETING_COLLECTION_ITEM_DRAG_TYPE,
      canDrop: (item: CatalogItemDragPayload) =>
        item.sourceCollectionId !== targetCollectionId,
      drop: (item: CatalogItemDragPayload) => {
        onDropItem(item);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [targetCollectionId, onDropItem],
  );

  const base = children.props.className ?? '';
  let className = base;
  if (isOver && canDrop) {
    className = `${base} ring-2 ring-blue-400 ring-inset bg-blue-50/50`.trim();
  } else if (isOver && !canDrop) {
    className = `${base} ring-2 ring-amber-200 ring-inset`.trim();
  }

  return React.cloneElement(children, {
    ref: (node: HTMLTableRowElement | null) => {
      drop(node);
    },
    className,
  });
}

type CatalogExpandedListDropZoneProps = {
  targetCollectionId: number;
  onDropItem: (payload: CatalogItemDragPayload) => void;
  className?: string;
  children: React.ReactNode;
};

export function CatalogExpandedListDropZone({
  targetCollectionId,
  onDropItem,
  className = '',
  children,
}: CatalogExpandedListDropZoneProps) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: MARKETING_COLLECTION_ITEM_DRAG_TYPE,
      canDrop: (item: CatalogItemDragPayload) =>
        item.sourceCollectionId !== targetCollectionId,
      drop: (item: CatalogItemDragPayload) => {
        onDropItem(item);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [targetCollectionId, onDropItem],
  );

  const zoneClass = [
    className,
    isOver && canDrop ? 'ring-2 ring-blue-400 ring-inset rounded-md bg-blue-50/30' : '',
    isOver && !canDrop ? 'ring-2 ring-amber-200 ring-inset rounded-md' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={drop} className={zoneClass}>
      {children}
    </div>
  );
}
